import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ExternalLink, RefreshCw, Search, Package as PackageIcon, AlertCircle, ChevronDown,
  Pencil, Link2, Download, Upload, Edit3, Send, Filter as FilterIcon, X, Eye,
} from "lucide-react";
import { MarketplaceOfferEditModal, GomagLinkModal, QuickPriceEditor } from "../components/MarketplaceOfferModals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tab labels follow the user's preferred naming from the eMAG seller panel.
// Order: BAU first (RO/BG/HU), then FBE (RO/BG/HU), then Trendyol (RO/BG).
const TABS = [
  { key: "emag_ro_bau", label: "Emag",        flag: "🇷🇴" },
  { key: "emag_bg_bau", label: "EmagBG",      flag: "🇧🇬" },
  { key: "emag_hu_bau", label: "EmagHU",      flag: "🇭🇺" },
  { key: "emag_ro_fbe", label: "EmagFBE",     flag: "🇷🇴" },
  { key: "emag_bg_fbe", label: "EmagBGFBE",   flag: "🇧🇬" },
  { key: "emag_hu_fbe", label: "EmagHUFBE",   flag: "🇭🇺" },
  { key: "trendyol_ro", label: "TrendyolRO",  flag: "🇷🇴" },
  { key: "trendyol_bg", label: "TrendyolBG",  flag: "🇧🇬" },
];

function formatPrice(p, currency) {
  if (p === null || p === undefined || p === "") return "—";
  const num = Number(p);
  if (!Number.isFinite(num)) return "—";
  return `${num.toFixed(2)} ${currency || ""}`.trim();
}

// Subtle pill badge used across the row (Detalii column + Acțiuni column).
function Pill({ tone = "zinc", children, testId }) {
  const map = {
    green: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
    blue:  "bg-sky-500/10 text-sky-300 border-sky-500/40",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/40",
    rose:  "bg-rose-500/10 text-rose-300 border-rose-500/40",
    zinc:  "bg-zinc-800/60 text-zinc-300 border-zinc-700",
  };
  return (
    <span data-testid={testId}
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold ${map[tone] || map.zinc}`}>
      {children}
    </span>
  );
}

function StockText({ stock }) {
  const n = Number(stock || 0);
  const color = n <= 0 ? "text-rose-400" : n < 5 ? "text-amber-300" : "text-emerald-300";
  return <span className={`font-semibold ${color}`}>Stoc: {n}</span>;
}

function OfferRow({ offer, checked, onToggle, onEdit, onLink, onUnlink, onQuickPrice, onToggleStatus, statusBusy }) {
  return (
    <tr data-testid={`offer-row-${offer.external_id}`} className="border-b border-zinc-800 hover:bg-zinc-900/40">
      {/* checkbox */}
      <td className="w-10 px-3 py-3 align-top">
        <input type="checkbox" checked={checked} onChange={onToggle}
          data-testid={`offer-check-${offer.external_id}`}
          className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-orange-500 focus:ring-orange-500/40" />
      </td>
      {/* imagine */}
      <td className="w-24 px-3 py-3 align-top">
        <div className="h-20 w-20 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
          {offer.main_image ? (
            <img src={offer.main_image} alt="" className="h-full w-full object-contain" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-zinc-700">
              <PackageIcon className="h-6 w-6" />
            </div>
          )}
        </div>
      </td>
      {/* nume */}
      <td className="min-w-[280px] max-w-[380px] px-3 py-3 align-top">
        <div className="flex items-start gap-2">
          <p className="flex-1 text-sm font-semibold leading-snug text-zinc-100" title={offer.name}>
            {offer.name || "—"}
          </p>
          <div className="flex flex-shrink-0 flex-col gap-1.5">
            <button onClick={onEdit} title="Editează (modal complet)"
              className="rounded p-1 text-sky-300 hover:bg-sky-500/10" data-testid={`offer-edit-${offer.external_id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {offer.product_url ? (
              <a href={offer.product_url} target="_blank" rel="noopener noreferrer"
                title="Deschide pe marketplace (eMAG/Trendyol)"
                data-testid={`offer-view-${offer.external_id}`}
                className="rounded p-1 text-emerald-300 hover:bg-emerald-500/10">
                <Eye className="h-3.5 w-3.5" />
              </a>
            ) : (
              <button disabled title="URL marketplace indisponibil"
                data-testid={`offer-view-${offer.external_id}`}
                className="cursor-not-allowed rounded p-1 text-zinc-700">
                <Eye className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
          <span className="text-zinc-500">ID: <span className="font-mono text-sky-300">{offer.external_id ?? "—"}</span></span>
          {offer.pnk && <span className="text-zinc-500">PNK: <span className="font-mono text-sky-300">{offer.pnk}</span></span>}
          <span className="text-zinc-500 capitalize">{offer.platform_label}</span>
          {offer.linked_gomag_part_number && <span className="text-emerald-300" title="Linkuit cu Gomag">↔ {offer.linked_gomag_part_number}</span>}
        </div>
        <div className="mt-1.5 text-[11px]"><StockText stock={offer.stock} /></div>
      </td>
      {/* pret CU TVA — click to quick-edit. Backend furnizează valorile
          gata calculate cu TVA în `price_with_vat` / `recommended_price_with_vat`
          / `best_offer_sale_price_with_vat`. Nu mai multiplicăm client-side
          fiindcă pe BG/HU `recommended_price` este DEJA stocat GROSS
          (fix 2026-07-22 pentru bugul dublu-TVA raportat pe BG FBE). */}
      <td className="min-w-[150px] px-3 py-3 align-top text-[12px]">
        <div className="flex items-center gap-1.5">
          <span className="text-zinc-500">Poziție:</span>
          <Pill tone="green" testId={`offer-pos-${offer.external_id}`}>{offer.position ?? "—"}</Pill>
        </div>
        <button onClick={onQuickPrice} data-testid={`offer-quick-price-${offer.external_id}`}
          className="mt-1.5 inline-flex flex-col items-start gap-0 rounded text-left hover:bg-zinc-900/60 px-1 py-0.5 -mx-1 -my-0.5">
          <span className="inline-flex items-center gap-1">
            <span className="font-semibold text-zinc-100">{formatPrice(offer.price_with_vat, offer.currency)}</span>
            {offer.recommended_price_with_vat != null && offer.recommended_price_with_vat !== offer.price_with_vat && (
              <span className="text-[11px] text-zinc-500 line-through">{formatPrice(offer.recommended_price_with_vat, offer.currency)}</span>
            )}
            <Edit3 className="h-2.5 w-2.5 text-zinc-500" />
          </span>
          <span className="text-[10px] text-zinc-500">fără TVA: {formatPrice(offer.price, offer.currency)}</span>
        </button>
        <p className="mt-0.5 text-[11px] text-zinc-500">Adaos: —</p>
        <p className="mt-1.5 text-[11px] text-zinc-500">Cel mai bun preț:</p>
        <p className="text-[12px] font-semibold text-zinc-200">{formatPrice(offer.best_offer_sale_price_with_vat ?? offer.price_with_vat, offer.currency)}</p>
        <p className="text-[10px] text-zinc-500">fără TVA: {formatPrice(offer.best_offer_sale_price ?? offer.price, offer.currency)}</p>
      </td>
      {/* detalii */}
      <td className="min-w-[180px] px-3 py-3 align-top text-[11px]">
        <p className="text-zinc-500">Deținător documentație:</p>
        <Pill tone={offer.ownership ? "green" : "zinc"}>{offer.ownership ? "Yes" : "—"}</Pill>
        <p className="mt-1.5 text-zinc-500">Status:</p>
        <Pill tone={offer.status === 1 ? "green" : "amber"}>{offer.status === 1 ? "Approved" : "Pending"}</Pill>
        <p className="mt-1.5 text-zinc-500">Preț:</p>
        <Pill tone={offer.is_saleable ? "green" : "rose"}>{offer.is_saleable ? "Saleable" : "Not saleable"}</Pill>
      </td>
      {/* status — Activat/Inactiv toggle care PUSHEAZĂ la marketplace */}
      <td className="w-24 px-3 py-3 align-top text-[11px]">
        {offer.integration_key?.startsWith("emag_") ? (
          <button
            onClick={() => onToggleStatus && onToggleStatus(offer)}
            disabled={statusBusy}
            data-testid={`offer-status-toggle-${offer.external_id}`}
            title={offer.status === 1
              ? "Click → DEZACTIVEZI (scoți de la vânzare pe eMAG)"
              : "Click → ACTIVEZI (pui la vânzare pe eMAG)"}
            className="group inline-flex flex-col items-start gap-1 rounded-md border border-zinc-800 px-1.5 py-1 hover:border-zinc-600 disabled:cursor-not-allowed disabled:opacity-50">
            <span className={`text-[11px] font-semibold ${offer.status === 1 ? "text-emerald-300" : "text-zinc-400"}`}>
              {statusBusy ? "..." : (offer.status === 1 ? "Activat" : "Inactiv")}
            </span>
            <span className={`relative inline-block h-5 w-9 rounded-full transition ${offer.status === 1 ? "bg-emerald-500/60" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${offer.status === 1 ? "left-4" : "left-0.5"}`} />
            </span>
          </button>
        ) : (
          <div title="Trendyol nu suportă activate/deactivate via API">
            <span className="text-zinc-500">{offer.status === 1 ? "Activat" : "Inactiv"}</span>
            <div className={`mt-1 inline-flex h-5 w-9 items-center rounded-full opacity-50 ${offer.status === 1 ? "bg-emerald-500/40" : "bg-zinc-700"}`}>
              <span className={`h-4 w-4 rounded-full bg-white transition ${offer.status === 1 ? "translate-x-4" : "translate-x-0.5"}`} />
            </div>
          </div>
        )}
      </td>
      {/* erori */}
      <td className="w-20 px-3 py-3 align-top text-[11px] text-zinc-500">—</td>
      {/* actiuni — Link/Linked + Un-link (per user spec) */}
      <td className="w-32 px-3 py-3 align-top">
        <div className="flex flex-col items-end gap-1.5">
          <Pill tone="green">Procesat</Pill>
          {offer.linked_gomag_part_number ? (
            <>
              <button onClick={onLink} title={`Re-leagă (curent: ${offer.linked_gomag_part_number})`}
                data-testid={`offer-link-${offer.external_id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-200 transition hover:bg-emerald-500/25">
                <Link2 className="h-3 w-3" /> Linked
              </button>
              <button onClick={onUnlink} title="Șterge link-ul către Gomag"
                data-testid={`offer-unlink-${offer.external_id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-[11px] font-medium text-rose-200 transition hover:bg-rose-500/20">
                <X className="h-3 w-3" /> Un-Link
              </button>
            </>
          ) : (
            <button onClick={onLink} title="Link cu produs Gomag"
              data-testid={`offer-link-${offer.external_id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[11px] font-medium text-sky-200 transition hover:bg-sky-500/20">
              <Link2 className="h-3 w-3" /> Link
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function MarketplaceOffers() {
  const [accounts, setAccounts] = useState([]);
  const [activeKey, setActiveKey] = useState(() => {
    try { return localStorage.getItem("marketplace_offers_tab") || "emag_ro_bau"; } catch { return "emag_ro_bau"; }
  });
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Top-level status filter (always visible). active | inactive | all
  const [statusFilter, setStatusFilter] = useState("active");
  const [linkFilter, setLinkFilter] = useState("all");  // all | linked | unlinked
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncReport, setSyncReport] = useState(null); // { accounts: [{key, fetched, saved, error}], trendyol: [...], errors: [...] }
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);

  // Modals: full-page editor + Gomag link picker + inline quick price.
  const [editingOffer, setEditingOffer] = useState(null);
  const [linkingOffer, setLinkingOffer] = useState(null);
  const [quickPriceOffer, setQuickPriceOffer] = useState(null);
  const [pushingStock, setPushingStock] = useState(false);

  // Filter fields (mirror eMAG seller panel filters)
  const [fProductId, setFProductId] = useState("");
  const [fName, setFName] = useState("");
  const [fBrand, setFBrand] = useState("");
  const [fPriceMin, setFPriceMin] = useState("");
  const [fPriceMax, setFPriceMax] = useState("");
  const [fStatus, setFStatus] = useState("all"); // all | active | inactive
  const [fStock, setFStock] = useState("all");   // all | in | out
  const [fSku, setFSku] = useState("");

  useEffect(() => {
    axios.get(`${API}/marketplace-offers/accounts`).then((r) => {
      setAccounts(r.data?.accounts || []);
    }).catch(() => setAccounts(TABS.map((t) => ({ ...t, key: t.key, is_active: false }))));
  }, []);

  useEffect(() => {
    try { localStorage.setItem("marketplace_offers_tab", activeKey); } catch (_e) { /* ignore */ }
    setSelectedIds(new Set());
  }, [activeKey]);

  const fetchOffers = async (key) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/marketplace-offers`, {
        params: { integration_key: key, status: statusFilter, link_status: linkFilter, ...(fName ? { search: fName } : {}) },
      });
      setOffers(r.data?.offers || []);
      setSyncedAt(r.data?.synced_at || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea ofertelor");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(activeKey); }, [activeKey, statusFilter, linkFilter]); // eslint-disable-line

  const handleUnlink = async (integrationKey, offerId, currentPart) => {
    if (!window.confirm(`Sigur dezlegi oferta de produsul Gomag ${currentPart || ""}?`)) return;
    try {
      await axios.delete(`${API}/marketplace-offers/${integrationKey}/${offerId}/link-gomag`);
      toast.success("Dezlegat");
      await fetchOffers(activeKey);
    } catch (e) {
      toast.error("Eroare unlink: " + (e?.response?.data?.detail || e.message));
    }
  };

  const [statusBusyId, setStatusBusyId] = useState(null);
  const handleToggleStatus = async (offer) => {
    if (!offer.integration_key?.startsWith("emag_")) {
      toast.error("Trendyol nu suportă activate/deactivate prin API.");
      return;
    }
    const next = offer.status === 1 ? 0 : 1;
    const verb = next === 1 ? "ACTIVEZI (pui la vânzare)" : "DEZACTIVEZI (scoți de la vânzare)";
    if (!window.confirm(
      `Sigur ${verb} oferta\n\n${offer.name}\n\npe ${offer.integration_key.toUpperCase()}?\n\nAcțiunea este trimisă imediat la eMAG.`
    )) return;
    const busyKey = `${offer.integration_key}:${offer.external_id}`;
    setStatusBusyId(busyKey);
    try {
      const r = await axios.put(
        `${API}/marketplace-offers/${offer.integration_key}/${offer.external_id}`,
        { status: next },
      );
      const push = r.data?.marketplace_push;
      const applied = push?.applied;
      if (applied) {
        toast.success(next === 1 ? "Activat pe eMAG ✓" : "Dezactivat pe eMAG ✓");
      } else if (push?.attempted) {
        toast.error(`Push eMAG eșuat: ${push.error || "vezi logs"}`);
      } else {
        toast.success("Salvat local (push neîncercat)");
      }
      await fetchOffers(activeKey);
    } catch (e) {
      toast.error("Eroare: " + (e?.response?.data?.detail || e.message));
    } finally {
      setStatusBusyId(null);
    }
  };


  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/marketplace-offers/sync`, null, { params: { integration_key: activeKey } });
      const fetched = r.data?.fetched ?? r.data?.saved ?? 0;
      toast.success(`Sincronizare reușită · ${fetched} oferte`);
      await fetchOffers(activeKey);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la sincronizare");
    } finally {
      setSyncing(false);
    }
  };

  const onSyncAll = async () => {
    setSyncingAll(true);
    setSyncReport(null);
    try {
      const r = await axios.post(`${API}/marketplace-offers/sync-all`);
      const jobId = r.data?.job_id;
      toast.info("Sync TOATE conturile pornit în fundal · durează ~2-3 minute");
      if (!jobId) {
        setSyncingAll(false);
        return;
      }
      const start = Date.now();
      const iv = setInterval(async () => {
        try {
          const jr = await axios.get(`${API}/marketplace-offers/sync-jobs/${jobId}`);
          if (jr.data?.status === "completed") {
            clearInterval(iv);
            setSyncReport(jr.data);
            const emagSaved = jr.data?.emag?.saved ?? 0;
            const emagFetched = jr.data?.emag?.fetched ?? 0;
            const accs = jr.data?.emag?.accounts || [];
            const accsWithErr = accs.filter((a) => a.error);
            const ty = jr.data?.trendyol || [];
            const tySaved = ty.reduce((s, x) => s + (x.saved || 0), 0);
            if (accsWithErr.length || (jr.data?.errors || []).length) {
              toast.warning(`Sync parțial · eMAG ${emagSaved}/${emagFetched}, Trendyol ${tySaved}. Vezi raportul de mai jos.`);
            } else {
              toast.success(`Sync TOATE conturile gata · eMAG ${emagSaved}/${emagFetched}, Trendyol ${tySaved}`);
            }
            setSyncingAll(false);
            await fetchOffers(activeKey);
          } else if (Date.now() - start > 6 * 60_000) {
            clearInterval(iv);
            setSyncingAll(false);
            toast.warning("Sync rulează încă · reîncarcă pagina peste 1-2 min");
          }
        } catch (_e) { /* keep polling */ }
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la sincronizare globală");
      setSyncingAll(false);
    }
  };

  const onDiagnostics = async () => {
    setLoadingDiag(true);
    setDiagnostics(null);
    try {
      const r = await axios.get(`${API}/marketplace-offers/diagnostics`);
      setDiagnostics(r.data?.accounts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic eșuat");
    } finally {
      setLoadingDiag(false);
    }
  };

  const onMigrateIndexes = async () => {
    if (!window.confirm("Migrează indexurile colecției products?\n\nDrop unique single-field _emag_id index (legacy) + recreate composite uniqueness. Sigur, idempotent.")) return;
    try {
      const r = await axios.post(`${API}/marketplace-offers/migrate-indexes`);
      const dropped = r.data?.dropped || [];
      if (dropped.length) toast.success(`Migrare reușită · indexuri șterse: ${dropped.join(", ")}`);
      else toast.info("Niciun index legacy de migrat — totul OK.");
      // eslint-disable-next-line no-console
      console.log("Indexes before:", r.data?.before, "after:", r.data?.after);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migrare eșuată");
    }
  };

  // Client-side filtering (server already returns active products)
  const filtered = useMemo(() => {
    return offers.filter((o) => {
      if (fProductId) {
        const q = fProductId.toLowerCase();
        if (!String(o.external_id || "").toLowerCase().includes(q)
            && !String(o.pnk || "").toLowerCase().includes(q)
            && !String(o.ean || "").toLowerCase().includes(q)) return false;
      }
      if (fName && !String(o.name || "").toLowerCase().includes(fName.toLowerCase())) return false;
      if (fBrand && !String(o.brand || "").toLowerCase().includes(fBrand.toLowerCase())) return false;
      if (fSku && !String(o.sku || "").toLowerCase().includes(fSku.toLowerCase())) return false;
      if (fPriceMin && Number(o.price || 0) < Number(fPriceMin)) return false;
      if (fPriceMax && Number(o.price || 0) > Number(fPriceMax)) return false;
      if (fStatus === "active" && o.status !== 1) return false;
      if (fStatus === "inactive" && o.status === 1) return false;
      if (fStock === "in" && Number(o.stock || 0) <= 0) return false;
      if (fStock === "out" && Number(o.stock || 0) > 0) return false;
      return true;
    });
  }, [offers, fProductId, fName, fBrand, fSku, fPriceMin, fPriceMax, fStatus, fStock]);

  const enriched = useMemo(() => filtered.map((o) => ({
    ...o,
    platform_label: activeKey.startsWith("emag_") ? "Emag" : "Trendyol",
    is_saleable: Number(o.stock || 0) > 0 && o.status === 1,
    ownership: !!o.product_url,
    pnk: o.pnk || (o.product_url && o.product_url.match(/\/pd\/([A-Z0-9]+)/)?.[1]) || "",
  })), [filtered, activeKey]);

  const allChecked = enriched.length > 0 && enriched.every((o) => selectedIds.has(String(o.external_id)));
  const toggleAll = () => {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(enriched.map((o) => String(o.external_id))));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      const k = String(id);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  const resetFilters = () => {
    setFProductId(""); setFName(""); setFBrand(""); setFPriceMin(""); setFPriceMax("");
    setFStatus("all"); setFStock("all"); setFSku("");
  };

  const merged = useMemo(() => TABS.map((t) => {
    const acc = accounts.find((a) => a.key === t.key);
    return { ...t, is_active: acc ? acc.is_active : false };
  }), [accounts]);

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="marketplace-offers-page">
      {/* Header + breadcrumb */}
      <div>
        <h1 className="text-xl font-semibold text-zinc-50">Oferte</h1>
        <p className="mt-0.5 text-[11px] text-zinc-500">Panou de control <span className="mx-1">›</span> Oferte</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-zinc-800 pb-0.5" data-testid="marketplace-tabs">
        {merged.map((tab) => {
          const isActive = activeKey === tab.key;
          return (
            <button
              key={tab.key}
              data-testid={`marketplace-tab-${tab.key}`}
              onClick={() => setActiveKey(tab.key)}
              disabled={!tab.is_active}
              title={tab.is_active ? tab.label : `${tab.label} (inactiv)`}
              className={`inline-flex items-center gap-1.5 rounded-t-lg border-b-2 px-4 py-2 text-xs font-medium transition ${
                isActive
                  ? "border-orange-400 bg-zinc-900 text-orange-200"
                  : tab.is_active
                  ? "border-transparent text-zinc-400 hover:bg-zinc-900/60 hover:text-zinc-200"
                  : "border-transparent text-zinc-700"
              }`}
            >
              <span className="text-[13px]">{tab.flag}</span>
              <span>{tab.label}</span>
              {!tab.is_active && <AlertCircle className="h-3 w-3" />}
            </button>
          );
        })}
        <div className="ml-auto pr-1">
          <button title="Setări tab" className="rounded p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200">
            <FilterIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filters panel */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60" data-testid="filters-panel">
        <button onClick={() => setShowFilters((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-semibold text-zinc-200">
          <span className="flex items-center gap-2"><FilterIcon className="h-3.5 w-3.5" /> Filtre</span>
          <ChevronDown className={`h-4 w-4 transition ${showFilters ? "rotate-180" : ""}`} />
        </button>
        {showFilters && (
          <div className="border-t border-zinc-800 px-4 py-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Identificare produs</label>
                <input value={fProductId} onChange={(e) => setFProductId(e.target.value)}
                  placeholder="Filtrare după ID, PNK sau EAN" data-testid="filter-product-id"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Nume</label>
                <input value={fName} onChange={(e) => setFName(e.target.value)}
                  placeholder="Filtrare după nume" data-testid="filter-name"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Brand</label>
                <input value={fBrand} onChange={(e) => setFBrand(e.target.value)}
                  placeholder="Filtrare după brand" data-testid="filter-brand"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Preț</label>
                <div className="flex items-center gap-1.5">
                  <input value={fPriceMin} onChange={(e) => setFPriceMin(e.target.value)} placeholder="de la" data-testid="filter-price-min"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
                  <span className="text-zinc-600">↔</span>
                  <input value={fPriceMax} onChange={(e) => setFPriceMax(e.target.value)} placeholder="până la" data-testid="filter-price-max"
                    className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Status</label>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 focus:border-orange-500/60 focus:outline-none">
                  <option value="all">Tot</option>
                  <option value="active">Activat</option>
                  <option value="inactive">Inactiv</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Disponibilitate</label>
                <select value={fStock} onChange={(e) => setFStock(e.target.value)} data-testid="filter-stock"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 focus:border-orange-500/60 focus:outline-none">
                  <option value="all">Tot</option>
                  <option value="in">În stoc</option>
                  <option value="out">Fără stoc</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500">SKU Produs</label>
                <input value={fSku} onChange={(e) => setFSku(e.target.value)}
                  placeholder="Filtrează după SKU Produs" data-testid="filter-sku"
                  className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-orange-500/60 focus:outline-none" />
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={resetFilters} data-testid="filter-reset"
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-3 py-1.5 text-[11px] font-medium text-zinc-300 hover:border-rose-500/40 hover:text-rose-300">
                <X className="h-3 w-3" /> Resetare filtru
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats + Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <p className="text-xs text-zinc-300">
            Total oferte: <span data-testid="offers-count" className="font-bold text-zinc-50">{enriched.length}</span>
            {syncedAt && (
              <span className="ml-3 text-[10px] text-zinc-500">
                · ultima sincronizare {new Date(syncedAt).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}
              </span>
            )}
          </p>
          {/* Segmented control: Activ / Inactiv / Toate */}
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-800" data-testid="status-segmented">
            {[
              { v: "active", label: "Activate" },
              { v: "inactive", label: "Inactive" },
              { v: "all", label: "Toate" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`status-seg-${opt.v}`}
                onClick={() => setStatusFilter(opt.v)}
                className={`px-3 py-1 text-[11px] font-medium transition ${
                  statusFilter === opt.v
                    ? "bg-orange-500/20 text-orange-200"
                    : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Segmented control: link status (Linkuit / Nelinkuit / Toate) */}
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-800" data-testid="link-segmented">
            {[
              { v: "all", label: "Toate" },
              { v: "linked", label: "Linkuite", icon: "🔗" },
              { v: "unlinked", label: "Nelinkuite", icon: "✗" },
            ].map((opt) => (
              <button
                key={opt.v}
                data-testid={`link-seg-${opt.v}`}
                onClick={() => setLinkFilter(opt.v)}
                title={opt.v === "linked" ? "Doar oferte conectate cu un produs Gomag" : opt.v === "unlinked" ? "Oferte fără link la Gomag" : "Toate"}
                className={`px-3 py-1 text-[11px] font-medium transition ${
                  linkFilter === opt.v
                    ? "bg-emerald-500/20 text-emerald-200"
                    : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                }`}
              >
                {opt.icon && <span className="mr-1">{opt.icon}</span>}
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button disabled={selectedIds.size === 0} data-testid="bulk-retrimite"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 transition hover:border-zinc-700 disabled:cursor-not-allowed disabled:opacity-40">
            <Send className="h-3 w-3" /> Retrimite
          </button>
          <button disabled={selectedIds.size === 0} data-testid="bulk-link-produs"
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 transition hover:border-zinc-700 disabled:cursor-not-allowed disabled:opacity-40">
            <Link2 className="h-3 w-3" /> Link produs
          </button>
          <button disabled={selectedIds.size === 0} data-testid="bulk-edit"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-medium text-emerald-200 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40">
            <Edit3 className="h-3 w-3" /> Editare în masă
          </button>
          <span className="mx-1 h-4 w-px bg-zinc-800" />
          <button onClick={onSync} disabled={syncing || syncingAll} data-testid="marketplace-offers-sync-btn"
            title={`Sincronizează doar tabul curent (${activeKey})`}
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 px-2.5 py-1.5 text-[11px] font-medium text-orange-200 transition hover:bg-orange-500/20 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizez..." : "Sincronizează tabul"}
          </button>
          <button onClick={onSyncAll} disabled={syncing || syncingAll} data-testid="marketplace-offers-sync-all-btn"
            title="Sincronizează toate cele 8 conturi simultan (eMAG + Trendyol)"
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/60 bg-orange-500/20 px-2.5 py-1.5 text-[11px] font-bold text-orange-100 transition hover:bg-orange-500/30 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${syncingAll ? "animate-spin" : ""}`} />
            {syncingAll ? "Sync TOATE..." : "Sync TOATE conturile"}
          </button>
          <button onClick={onDiagnostics} disabled={loadingDiag} data-testid="marketplace-offers-diag-btn"
            title="Testează autentificarea pe fiecare cont marketplace"
            className="hidden">
            {loadingDiag ? "Verific..." : "Diagnostic"}
          </button>
          <button onClick={async () => {
            setPushingStock(true);
            try {
              const r = await axios.post(`${API}/marketplace-offers/push-oblio-stock`);
              const s = r.data?.summary || {};
              const tot = Object.values(s).reduce((acc, x) => acc + (x.pushed || 0), 0);
              toast.success(`Push stoc Oblio finalizat. ${tot} oferte actualizate.`);
            } catch (e) {
              toast.error("Eroare push: " + (e.response?.data?.detail || e.message));
            } finally { setPushingStock(false); }
          }} disabled={pushingStock} data-testid="marketplace-push-oblio-stock-btn"
            title="Trimite imediat stocul din Oblio (minus rezervări) la BAU + Trendyol (FBE skip)"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-medium text-emerald-200 transition hover:bg-emerald-500/20 disabled:opacity-50">
            <Send className={`h-3 w-3 ${pushingStock ? "animate-pulse" : ""}`} />
            {pushingStock ? "Push..." : "Push stoc Oblio"}
          </button>
          <button
            onClick={async () => {
              // First: dry-run to preview what would be healed.
              try {
                const preview = await axios.post(`${API}/marketplace-offers/heal-corrupt-prices`, {});
                const found = preview.data.found_count || 0;
                if (found === 0) {
                  toast.info("Nu s-au găsit prețuri corupte. DB e curat! ✅");
                  return;
                }
                const items = preview.data.items || [];
                const sample = items.slice(0, 5).map(x => `${x.integration_key}/${x.sku}: ${x.sale_price_old_gross} → ${x.sale_price_new_gross} ${x.currency}`).join("\n");
                const ok = window.confirm(
                  `S-au detectat ${found} prețuri corupte.\n\nPrimele câteva:\n${sample}${items.length > 5 ? "\n..." : ""}\n\nRepar toate acum? (sale_price se resetează la best_offer_sale_price din eMAG)`
                );
                if (!ok) return;
                const r = await axios.post(`${API}/marketplace-offers/heal-corrupt-prices`, { apply: true });
                toast.success(`✅ ${r.data.applied} prețuri reparate. Refresh...`);
                setTimeout(() => window.location.reload(), 1500);
              } catch (e) {
                toast.error("Eroare la detectare/reparare: " + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="marketplace-heal-prices-btn"
            title="Detectează și repară prețuri corupte (sale_price × TVA > recommended × 2 și best_offer diferit)"
            className="hidden"
          >
            Repară prețuri
          </button>
          <button onClick={onMigrateIndexes} data-testid="marketplace-offers-migrate-btn"
            title="Fix E11000 duplicate key error — drop indexul vechi unique pe _emag_id"
            className="hidden">
            Migrează indexuri
          </button>
        </div>
      </div>

      {/* Sync Report Panel (after Sync TOATE) */}
      {syncReport && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4" data-testid="sync-report">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-100">Raport sincronizare</h3>
            <button onClick={() => setSyncReport(null)} className="text-[11px] text-zinc-500 hover:text-zinc-300">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-2">
            {(syncReport?.emag?.accounts || []).map((a) => (
              <div key={a.key} data-testid={`sync-result-${a.key}`}
                className={`flex items-center justify-between rounded-md border px-3 py-2 text-[11px] ${
                  a.error ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/30 bg-emerald-500/5"
                }`}>
                <span className="font-mono text-zinc-300">{a.name || a.key}</span>
                {a.error ? (
                  <span className="text-rose-300">{a.error.substring(0, 200)}</span>
                ) : (
                  <span className="text-emerald-300">{a.fetched} fetched · {a.saved} saved</span>
                )}
              </div>
            ))}
            {(syncReport?.trendyol || []).map((a) => (
              <div key={a.key} className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px]">
                <span className="font-mono text-zinc-300">{a.key}</span>
                <span className="text-emerald-300">{a.fetched} fetched · {a.saved} saved</span>
              </div>
            ))}
            {(syncReport?.errors || []).map((er, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-[11px]">
                <span className="font-mono text-zinc-300">{er.scope}</span>
                <span className="text-rose-300">{(er.detail || "").substring(0, 250)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Diagnostic Panel (live auth probe) */}
      {diagnostics && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4" data-testid="diagnostics-panel">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-100">Diagnostic conturi marketplace</h3>
            <button onClick={() => setDiagnostics(null)} className="text-[11px] text-zinc-500 hover:text-zinc-300">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-3 text-[11px] text-zinc-500">
            Ping live pe API-ul fiecărui marketplace. Dacă vezi <span className="text-rose-300">eroare</span>,
            atunci sync-ul nu poate funcționa pentru acel cont — vezi mesajul de mai jos pentru cauză
            (401 = credențiale greșite, 403 = IP nu e whitelist-uit la eMAG, etc.).
          </p>
          <div className="grid gap-2">
            {diagnostics.map((d) => (
              <div key={d.key} data-testid={`diag-${d.key}`}
                className={`flex flex-col gap-1 rounded-md border px-3 py-2 text-[11px] md:flex-row md:items-center md:justify-between ${
                  d.error ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/30 bg-emerald-500/5"
                }`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-zinc-200">{d.flag} {d.name}</span>
                  <Pill tone={d.is_active ? "green" : "zinc"}>{d.is_active ? "Activ" : "Inactiv"}</Pill>
                  <Pill tone={d.is_configured ? "green" : "rose"}>{d.is_configured ? "Credentials OK" : "Fără credentials"}</Pill>
                  <span className="text-zinc-500">{d.products_cached} produse în cache</span>
                </div>
                <span className={d.error ? "text-rose-300" : "text-emerald-300"}>
                  {d.error
                    ? d.error.substring(0, 220)
                    : d.probe?.total != null
                      ? `OK · total catalog: ${d.probe.total}`
                      : "OK"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/60" data-testid="offers-list">
        <table className="w-full border-collapse">
          <thead className="bg-zinc-900/60">
            <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <th className="w-10 px-3 py-2.5">
                <input type="checkbox" checked={allChecked} onChange={toggleAll}
                  data-testid="offers-check-all"
                  className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-orange-500" />
              </th>
              <th className="px-3 py-2.5">Imagine</th>
              <th className="px-3 py-2.5">Nume</th>
              <th className="px-3 py-2.5">Preț cu TVA</th>
              <th className="px-3 py-2.5">Detalii</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Erori</th>
              <th className="px-3 py-2.5 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-zinc-500" data-testid="offers-loading">Se încarcă ofertele...</td></tr>
            ) : enriched.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-zinc-500" data-testid="offers-empty">
                <PackageIcon className="mx-auto mb-2 h-8 w-8 text-zinc-700" />
                {offers.length === 0
                  ? <>Nicio ofertă activă în acest cont. Apasă <span className="text-orange-300">Sincronizează</span> pentru a importa produsele.</>
                  : "Niciun rezultat pentru filtrele aplicate."}
              </td></tr>
            ) : enriched.map((o) => (
              <OfferRow key={`${o.integration_key}-${o.external_id}`}
                offer={o}
                checked={selectedIds.has(String(o.external_id))}
                onToggle={() => toggleOne(o.external_id)}
                onEdit={() => setEditingOffer({ integrationKey: o.integration_key, offerId: o.external_id })}
                onLink={() => setLinkingOffer({ integrationKey: o.integration_key, offerId: o.external_id, currentLink: o.linked_gomag_part_number, prefillSku: o.sku, prefillEan: o.ean, prefillPnk: o.pnk })}
                onUnlink={() => handleUnlink(o.integration_key, o.external_id, o.linked_gomag_part_number)}
                onQuickPrice={() => setQuickPriceOffer({ integrationKey: o.integration_key, offerId: o.external_id, currentPrice: o.price_with_vat != null ? +Number(o.price_with_vat).toFixed(2) : null, currentStock: o.stock, currency: o.currency, vat: o.vat_rate })}
                onToggleStatus={() => handleToggleStatus(o)}
                statusBusy={statusBusyId === `${o.integration_key}:${o.external_id}`}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {editingOffer && (
        <MarketplaceOfferEditModal
          integrationKey={editingOffer.integrationKey}
          offerId={editingOffer.offerId}
          onClose={() => setEditingOffer(null)}
          onSaved={() => fetchOffers(activeKey)}
        />
      )}
      {linkingOffer && (
        <GomagLinkModal
          integrationKey={linkingOffer.integrationKey}
          offerId={linkingOffer.offerId}
          currentLink={linkingOffer.currentLink}
          prefillSku={linkingOffer.prefillSku}
          prefillEan={linkingOffer.prefillEan}
          prefillPnk={linkingOffer.prefillPnk}
          onClose={() => setLinkingOffer(null)}
          onLinked={() => fetchOffers(activeKey)}
        />
      )}
      {quickPriceOffer && (
        <QuickPriceEditor
          integrationKey={quickPriceOffer.integrationKey}
          offerId={quickPriceOffer.offerId}
          currentPrice={quickPriceOffer.currentPrice}
          currentStock={quickPriceOffer.currentStock}
          currency={quickPriceOffer.currency}
          vat={quickPriceOffer.vat}
          onClose={() => setQuickPriceOffer(null)}
          onSaved={() => fetchOffers(activeKey)}
        />
      )}
    </div>
  );
}
